import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from "react";

/**
 * Keeps a {@link HTMLTextAreaElement} height equal to its content so the browser
 * does not show an internal scrollbar. Call the returned ref callback on the textarea.
 *
 * When {@link mirrorRef} is set (tag highlight mirror behind a transparent textarea),
 * height uses `max(scrollHeight, mirror.offsetHeight)` so the stacked layout stays
 * aligned when inline marks change line metrics or when focus triggers a remeasure.
 * Pass {@link mirrorEnabled} when the mirror layer is actually mounted so ResizeObserver
 * and measurements stay in sync when tag highlights toggle on/off.
 * Pass {@link mirrorLayoutKey} when tag spans or pending selection can change height without
 * the textarea `value` string changing.
 *
 * When {@link maxHeightPx} is set, height grows with content until that cap; overflow becomes
 * scrollable vertically beyond the cap.
 */
export function useAutosizeTextarea(
  value: string,
  canvasScale: number,
  mirrorRef?: RefObject<HTMLElement | null>,
  mirrorEnabled = false,
  /** Bumped when mirror content can change without `value` changing (e.g. tag spans). */
  mirrorLayoutKey?: string,
  maxHeightPx?: number
) {
  const elementRef = useRef<HTMLTextAreaElement | null>(null);

  const syncHeight = useCallback(() => {
    const el = elementRef.current;
    if (!el) return;
    el.style.overflow = "hidden";
    el.style.height = "0px";
    const scrollH = el.scrollHeight;
    const mirror = mirrorEnabled ? (mirrorRef?.current ?? null) : null;
    const mirrorH = mirror?.offsetHeight ?? 0;
    let next = mirrorEnabled && mirror != null ? Math.max(scrollH, mirrorH) : scrollH;
    if (maxHeightPx != null && next > maxHeightPx) {
      next = maxHeightPx;
      el.style.overflowY = "auto";
    } else {
      el.style.overflowY = "hidden";
    }
    el.style.height = `${next}px`;
  }, [maxHeightPx, mirrorEnabled, mirrorRef]);

  useLayoutEffect(() => {
    syncHeight();
  }, [value, canvasScale, syncHeight, mirrorLayoutKey]);

  useEffect(() => {
    window.addEventListener("resize", syncHeight);
    return () => window.removeEventListener("resize", syncHeight);
  }, [syncHeight]);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;
    const onFocus = () => syncHeight();
    el.addEventListener("focus", onFocus);
    return () => el.removeEventListener("focus", onFocus);
  }, [syncHeight]);

  useEffect(() => {
    if (!mirrorEnabled) return;
    const mirror = mirrorRef?.current;
    if (!mirror) return;
    const ro = new ResizeObserver(() => {
      syncHeight();
    });
    ro.observe(mirror);
    return () => ro.disconnect();
  }, [mirrorEnabled, mirrorRef, syncHeight]);

  const assignRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      elementRef.current = node;
      if (node) {
        requestAnimationFrame(() => {
          syncHeight();
        });
      }
    },
    [syncHeight]
  );

  return assignRef;
}
